<template>
  <div
    class="moniex-viewport has-isin-info"
    :class="{ 'about-bento-dark': isDarkMode }"
  >
    <div class="moniex-glow-orb"></div>

    <div class="row d-flex flex-wrap isin-info-row">
      <div
        v-for="game in openGamesPreview"
        :key="game.GameID"
        class="col-lg-3 col-md-6 col-12"
      >
        <open-game-mini-card
          :game-id="game.GameID"
          :game-name="game.GameName"
          :game-register="game.GameRegister"
          :game-reward-count="game.GameRewardCount"
          :game-reward-volume="game.GameRewardVolume"
          :game-reward-img="game.GameRewardImg"
          :game-reward-imgs="game.RewardImgs"
        />
      </div>
    </div>

    <div class="moniex-bento-board" ref="boardRef" :style="boardStyle">
      <div
        class="moniex-premium-card card-inga-54 img-inga-card is-game-link"
        :style="ingaStyle"
        role="link"
        tabindex="0"
        @click="openMarketingGame"
        @keydown.enter="openMarketingGame"
      >
        <div v-if="bubbleText" class="inga-speech-bubble" v-html="bubbleText"></div>
        <IngaPortrait :src="imgRoot + 'inga.jpg'" />
      </div>

      <div
        class="moniex-premium-card card-story-54 is-game-link"
        ref="storyRef"
        role="link"
        tabindex="0"
        @click="openMarketingGame"
        @keydown.enter="openMarketingGame"
      >
        <div class="card-header-moniex">
          <span class="card-title-main" v-html="storyTitle"></span>
          <span class="card-meta-badge badge-story">Příběh</span>
        </div>
        <div class="card-body-text">
          <div v-html="storyText"></div>
        </div>
      </div>

      <div
        class="moniex-premium-card card-account-mid is-game-link"
        ref="accountRef"
        :style="accountStyle"
        role="link"
        tabindex="0"
        @click="openMarketingGame"
        @keydown.enter="openMarketingGame"
      >
        <div class="account-reward" v-if="rewardUrls.length">
          <IngaPortrait
            frame="reward"
            :src="rewardUrls[0]"
            :sources="rewardUrls"
            :alt="rewardName || accountTitle"
            rotate
            :fade-ms="3200"
            :rotate-ms="4800"
            :lightbox="false"
          />
        </div>
        <div v-else class="moniex-icon-wrapper">
          <i class="ni ni-credit-card text-lg opacity-10 text-orange"></i>
        </div>
        <div class="moniex-card-content w-100">
          <span class="moniex-label">{{ accountTitle }}</span>
          <span v-if="rewardName" class="account-reward-name">{{ rewardName }}</span>
          <div
            v-if="rewardSnippet"
            class="account-text-format"
            v-html="rewardSnippet"
          ></div>
          <div
            v-else-if="accountSnippet"
            class="account-text-format"
            v-html="accountSnippet"
          ></div>
        </div>
      </div>

      <div
        class="moniex-premium-card card-contact-bottom is-game-link"
        ref="mediaRef"
        :style="mediaStyle"
        role="link"
        tabindex="0"
        @click="onBubbleClick"
        @keydown.enter="openMarketingGame"
      >
        <div class="moniex-icon-wrapper">
          <i class="ni ni-world-2 text-lg opacity-10 text-orange"></i>
        </div>
        <div class="moniex-card-content w-100">
          <span class="moniex-label">{{ mediaTitle }}</span>
          <div class="api-html-content" v-html="mediaText"></div>
        </div>
      </div>
    </div>

    <p class="marketing-disclaimer">
      Texty mohou obsahovat reklamní nadsázku. Přesné parametry jsou uvedeny u výhry.
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { useStore } from "vuex";
import dataService from "@/services/dataService.js";
import IngaPortrait from "./components/custom/IngaPortrait.vue";
import OpenGameMiniCard from "./components/custom/OpenGameMiniCard.vue";

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const marketing = ref({});
const imgRoot = computed(() => process.env.VUE_APP_IMG_ROOT || "/");
const OpenGame = computed(() => store.state.OpenGame);
const openGamesPreview = computed(() => (Array.isArray(OpenGame.value) ? OpenGame.value.slice(0, 4) : []));

const bubbleText = computed(() => marketing.value.MarketingIngaText || "");
const storyTitle = computed(() => marketing.value.MarketingStoryTitle || "Příběh");
const storyText = computed(() => marketing.value.MarketingStoryText || "");
const accountTitle = computed(() => marketing.value.MarketingAccountTitle || "Účet");
const accountText = computed(() => marketing.value.MarketingAccountText || "");
const rewardName = computed(() => marketing.value.RewardName || "");
const rewardText = computed(() => marketing.value.RewardText || "");
const clipHtml = (html, limit = 200) => {
  const src = String(html || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<(?!\/?(?:strong|b)\b)[^>]*>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  let visible = 0;
  let out = "";
  let i = 0;
  const open = [];
  while (i < src.length && visible < limit) {
    if (src[i] === "<") {
      const end = src.indexOf(">", i);
      if (end < 0) break;
      const tag = src.slice(i, end + 1);
      const m = tag.match(/^<\/?(strong|b)\b/i);
      if (m) {
        out += tag;
        if (tag[1] === "/") open.pop();
        else open.push(m[1].toLowerCase());
      }
      i = end + 1;
      continue;
    }
    out += src[i];
    visible += 1;
    i += 1;
  }
  if (i < src.length) {
    const rest = src.slice(i);
    const space = rest.search(/\s/);
    if (space > 0 && space <= 24) {
      out += rest.slice(0, space);
      i += space;
    }
    while (open.length) out += `</${open.pop()}>`;
    return `${out.trim()}...`;
  }
  while (open.length) out += `</${open.pop()}>`;
  return out.trim();
};

const rewardSnippet = computed(() => clipHtml(rewardText.value, 200));
const accountSnippet = computed(() => clipHtml(accountText.value, 200));
const mediaTitle = computed(() => marketing.value.MarketingMediaTitle || "Média");
const mediaText = computed(() => marketing.value.MarketingMediaText || "");
const marketingGameId = computed(() => marketing.value.GameID);

const openMarketingGame = () => {
  if (!marketingGameId.value) return;
  store.dispatch("loadOpenGameDetail", marketingGameId.value);
};

const onBubbleClick = (event) => {
  if (event?.target?.closest?.("a")) return;
  openMarketingGame();
};

const apiRoot = computed(() => process.env.VUE_APP_API_URL);
const rewardFileUrl = (file) => {
  const value = String(file || "").trim();
  if (!value) return "";
  if (value.startsWith("http")) return value;
  if (value.startsWith("img/")) return apiRoot.value + value;
  return apiRoot.value + "img/" + value;
};
const rewardUrls = computed(() => {
  const extras = Array.isArray(marketing.value.RewardImgs) ? marketing.value.RewardImgs : [];
  const files = extras.map((item) => String(item || "").trim()).filter(Boolean);
  const cover = String(marketing.value.GameRewardImg || "").trim();
  if (cover && !files.includes(cover) && !files.some((item) => item.endsWith(cover.replace(/^img\//, "")))) {
    files.unshift(cover);
  }
  const unique = [];
  files.forEach((file) => {
    const url = rewardFileUrl(file);
    if (url && !unique.includes(url)) unique.push(url);
  });
  return unique;
});

const boardRef = ref(null);
const storyRef = ref(null);
const accountRef = ref(null);
const mediaRef = ref(null);
const accountTop = ref(340);
const mediaTop = ref(500);
const boardHeight = ref(1050);
const ingaHeight = ref(520);
const isNarrow = ref(false);

const accountStyle = computed(() => (isNarrow.value ? {} : { top: accountTop.value + "px" }));
const mediaStyle = computed(() => (isNarrow.value ? {} : { top: mediaTop.value + "px" }));
const boardStyle = computed(() => (isNarrow.value ? {} : { height: boardHeight.value + "px" }));
const ingaStyle = computed(() => (isNarrow.value ? {} : { "--inga-h": ingaHeight.value + "px" }));

const layoutStack = () => {
  if (typeof window === "undefined") return;
  isNarrow.value = window.innerWidth <= 992;
  if (isNarrow.value) return;
  const story = storyRef.value;
  const account = accountRef.value;
  const media = mediaRef.value;
  const board = boardRef.value;
  if (!story || !account || !media || !board) return;

  const gap = 24;
  const accTop = story.offsetTop + story.offsetHeight + gap;
  const accH = account.offsetHeight || 210;
  const medTop = accTop + accH + gap;
  const medH = media.offsetHeight || 180;

  accountTop.value = Math.round(accTop);
  mediaTop.value = Math.round(medTop);

  const ingaEl = board.querySelector(".card-inga-54");
  const ingaTop = ingaEl ? ingaEl.offsetTop : 46;
  const mediaBottom = medTop + medH;
  ingaHeight.value = Math.max(520, Math.round(mediaBottom - ingaTop));
  boardHeight.value = Math.round(Math.max(mediaBottom, ingaTop + ingaHeight.value) + 48);
};

let storyObserver = null;

onMounted(async () => {
  store.dispatch("loadOpenGames");
  try {
    marketing.value = (await dataService.GetMarketing()) || {};
  } catch (error) {
    console.error("Chyba načítání marketing dat z API:", error);
  }
  await nextTick();
  layoutStack();
  if (typeof ResizeObserver !== "undefined") {
    storyObserver = new ResizeObserver(() => layoutStack());
    if (storyRef.value) storyObserver.observe(storyRef.value);
    if (accountRef.value) storyObserver.observe(accountRef.value);
  }
  window.addEventListener("resize", layoutStack);
});

watch(marketing, async () => {
  await nextTick();
  layoutStack();
});

onBeforeUnmount(() => {
  if (storyObserver) storyObserver.disconnect();
  window.removeEventListener("resize", layoutStack);
});
</script>

<style scoped>
.moniex-viewport {
  position: relative !important;
  background-color: #ffedd5 !important;
  background:
    radial-gradient(circle at 0% 0%, #fff7ed 0%, transparent 60%),
    radial-gradient(circle at 100% 100%, #fdba74 0%, transparent 70%),
    linear-gradient(225deg, #fff7ed 0%, #ffedd5 40%, #fed7aa 100%) !important;
  min-height: 100vh !important;
  padding: 40px !important;
  width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: clip !important;
  overflow-y: visible !important;
  border-radius: 28px;
}

.has-isin-info {
  padding-top: calc(0.75rem * 0.36125 + 8px) !important;
}

.isin-info-row {
  position: relative;
  z-index: 2;
  padding-bottom: 36px;
}

.about-bento-dark.moniex-viewport {
  background: #1E293B !important;
  background-color: #1E293B !important;
}

.moniex-glow-orb {
  position: absolute !important;
  bottom: -50px !important;
  right: -50px !important;
  width: 500px !important;
  height: 500px !important;
  background: radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%) !important;
  filter: blur(50px) !important;
  z-index: 0 !important;
  pointer-events: none !important;
}

.about-bento-dark .moniex-glow-orb {
  background: radial-gradient(circle, rgba(15, 23, 42, 0.7) 0%, rgba(15, 23, 42, 0) 70%) !important;
}

.moniex-bento-board {
  position: relative !important;
  width: 100% !important;
  min-height: 700px !important;
  z-index: 1 !important;
}

.moniex-premium-card {
  position: absolute !important;
  background: #ffffff !important;
  border-radius: 28px !important;
  padding: 28px !important;
  box-shadow: 0 25px 50px rgba(154, 52, 0, 0.15) !important;
  border: none !important;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box !important;
}

.about-bento-dark .moniex-premium-card {
  background: #0F172A !important;
  box-shadow: none !important;
  border: 1px solid #334155 !important;
}

.about-bento-dark .card-story-54,
.about-bento-dark .card-account-mid,
.about-bento-dark .card-contact-bottom {
  border: 1px solid #334155 !important;
}

.about-bento-dark .card-story-54 {
  border-right: 6px solid #5B82AC !important;
}

.about-bento-dark .card-account-mid {
  border-right: 6px solid #5A8E65 !important;
}

.about-bento-dark .card-contact-bottom {
  border-right: 6px solid #B3794D !important;
}

.moniex-premium-card:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 35px 70px rgba(154, 52, 0, 0.22) !important;
  z-index: 60 !important;
}

.is-game-link {
  cursor: pointer !important;
}

.about-bento-dark .moniex-premium-card:hover {
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.28) !important;
}

.card-inga-54:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 35px 70px rgba(154, 52, 0, 0.22) !important;
  z-index: 10 !important;
}

.about-bento-dark .card-inga-54:hover {
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.28) !important;
  z-index: 10 !important;
}

.card-story-54 {
  top: 20px !important;
  right: 20px !important;
  left: auto !important;
  width: 54% !important;
  height: auto !important;
  min-height: 0 !important;
  z-index: 20 !important;
  background: #f8fafc !important;
  border-right: 6px solid #5B82AC !important;
}

.about-bento-dark .card-story-54 {
  background: #0F172A !important;
  border-right: 6px solid #5B82AC !important;
}

.card-inga-54 {
  top: 46px !important;
  left: 20px !important;
  width: 49% !important;
  height: var(--inga-h, 520px) !important;
  z-index: 10 !important;
  justify-content: flex-start !important;
  padding: 0 !important;
}

.card-inga-54 :deep(.inga-portrait-wrap) {
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  overflow: hidden;
}

.card-inga-54 :deep(.cartoon-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}

.about-bento-dark .card-inga-54 {
  background: #0F172A !important;
}

.card-account-mid {
  right: 10% !important;
  left: auto !important;
  width: 560px !important;
  min-height: 210px !important;
  height: auto !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 16px !important;
  z-index: 35 !important;
  border-right: 6px solid #5A8E65 !important;
}

.account-reward {
  width: 188px !important;
  height: 188px !important;
  flex-shrink: 0 !important;
  border-radius: 18px !important;
  overflow: hidden !important;
}

.account-reward :deep(.inga-portrait-wrap) {
  width: 188px;
  height: 188px;
  aspect-ratio: auto;
  border-radius: 18px;
}

.account-reward :deep(.cartoon-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-contact-bottom {
  right: 6% !important;
  left: auto !important;
  width: 380px !important;
  min-height: 180px !important;
  height: auto !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  gap: 18px !important;
  z-index: 36 !important;
  border-right: 6px solid #B3794D !important;
}

.moniex-icon-wrapper {
  background: #fff3e6 !important;
  width: 52px !important;
  height: 52px !important;
  border-radius: 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

.about-bento-dark .moniex-icon-wrapper {
  background: #1E293B !important;
}

.moniex-icon-wrapper i {
  font-size: 22px !important;
}

.card-account-mid .moniex-icon-wrapper {
  background: rgba(40, 167, 69, 0.08) !important;
}
.card-account-mid .moniex-icon-wrapper i {
  color: #28a745 !important;
}

.card-contact-bottom .moniex-icon-wrapper {
  background: rgba(255, 107, 0, 0.08) !important;
}
.card-contact-bottom .moniex-icon-wrapper i {
  color: #ff6b00 !important;
}

.about-bento-dark .card-account-mid .moniex-icon-wrapper,
.about-bento-dark .card-contact-bottom .moniex-icon-wrapper {
  background: #1E293B !important;
}

.card-header-moniex {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  margin-bottom: 12px !important;
}

.card-title-main {
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: #FF6B21 !important;
}

.card-title-main :deep(strong),
.card-title-main :deep(b) {
  color: #FF6B21 !important;
  font-weight: 800 !important;
}

.about-bento-dark .card-title-main {
  color: #FF8A4C !important;
}

.about-bento-dark .card-title-main :deep(strong),
.about-bento-dark .card-title-main :deep(b) {
  color: #FF8A4C !important;
}

.card-meta-badge {
  font-size: 0.75rem !important;
  background: #e2e8f0 !important;
  color: #475569 !important;
  padding: 4px 12px !important;
  border-radius: 20px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.badge-story {
  background: rgba(91, 130, 172, 0.16) !important;
  color: #5B82AC !important;
}

.about-bento-dark .card-meta-badge {
  background: #1E293B !important;
  color: #94A3B8 !important;
}

.about-bento-dark .badge-story {
  background: rgba(91, 130, 172, 0.22) !important;
  color: #7BA3C9 !important;
}

.card-body-text :deep(p),
.card-body-text :deep(div) {
  color: #334155 !important;
  line-height: 1.55 !important;
  font-size: 0.85rem !important;
  margin-bottom: 8px !important;
}

.card-body-text :deep(strong),
.card-body-text :deep(b) {
  color: #FF6B21 !important;
  font-weight: 800 !important;
}

.about-bento-dark .card-body-text :deep(p),
.about-bento-dark .card-body-text :deep(div),
.about-bento-dark .card-body-text :deep(i) {
  color: #CBD5E1 !important;
  background: transparent !important;
}

.about-bento-dark .card-body-text :deep(strong),
.about-bento-dark .card-body-text :deep(b) {
  color: #FF8A4C !important;
}

.api-html-content :deep(strong),
.api-html-content :deep(b),
.inga-speech-bubble :deep(strong),
.inga-speech-bubble :deep(b) {
  font-weight: 800 !important;
  color: #FF6B21 !important;
}

.about-bento-dark .api-html-content :deep(strong),
.about-bento-dark .api-html-content :deep(b),
.about-bento-dark .inga-speech-bubble :deep(strong),
.about-bento-dark .inga-speech-bubble :deep(b) {
  color: #FF8A4C !important;
}

.api-html-content :deep(p),
.api-html-content {
  font-size: 0.78rem !important;
  color: #334155 !important;
  line-height: 1.7 !important;
  margin: 0 !important;
}

.about-bento-dark .api-html-content,
.about-bento-dark .api-html-content :deep(p),
.about-bento-dark .api-html-content :deep(i) {
  color: #CBD5E1 !important;
}

.account-reward-name {
  display: block;
  font-weight: 800;
  font-size: 0.88rem;
  line-height: 1.25;
  color: #0F172A;
  margin: 2px 0 4px;
}

.about-bento-dark .account-reward-name {
  color: #E2E8F0;
}

.card-account-mid .account-text-format {
  font-size: 0.78rem !important;
  line-height: 1.7 !important;
  color: #334155 !important;
}

.about-bento-dark .card-account-mid .account-text-format {
  color: #CBD5E1 !important;
}

.account-text-format :deep(strong),
.account-text-format :deep(b) {
  font-size: inherit !important;
  font-weight: 800 !important;
  color: #FF6B21 !important;
  display: inline !important;
  margin-top: 0 !important;
  letter-spacing: 0 !important;
}

.about-bento-dark .account-text-format :deep(strong),
.about-bento-dark .account-text-format :deep(b) {
  color: #FF8A4C !important;
}

.moniex-label {
  display: block !important;
  font-size: 0.95rem !important;
  color: #64748b !important;
  font-weight: 800 !important;
  line-height: 1.3 !important;
  margin-bottom: 8px !important;
}

.about-bento-dark .moniex-label {
  color: #94a3b8 !important;
}

.inga-speech-bubble {
  position: absolute !important;
  top: -45px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  background-color: #ffffff !important;
  border: 2px solid #FF7A00 !important;
  color: #1E293B !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  padding: 12px 16px !important;
  border-radius: 18px !important;
  box-shadow: 0 10px 25px rgba(90, 30, 0, 0.08) !important;
  z-index: 15 !important;
  width: 90% !important;
  max-width: 290px !important;
  text-align: center !important;
}

.about-bento-dark .inga-speech-bubble {
  background-color: #1E293B !important;
  color: #E2E8F0 !important;
}

.inga-speech-bubble::after, .inga-speech-bubble::before {
  content: '' !important; position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%); border-style: solid; display: block; width: 0;
}
.inga-speech-bubble::after { border-width: 10px 10px 0; border-color: #ffffff transparent transparent; z-index: 11; }
.inga-speech-bubble::before { bottom: -13px; border-width: 11px 11px 0; border-color: #FF7A00 transparent transparent; z-index: 9; }
.about-bento-dark .inga-speech-bubble::after { border-color: #1E293B transparent transparent; }

.marketing-disclaimer {
  position: relative;
  z-index: 2;
  margin: 20px 0 0;
  padding: 0 8px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.4;
  color: #64748b;
  white-space: nowrap;
}

.about-bento-dark .marketing-disclaimer {
  color: #94a3b8;
}

@media (max-width: 992px) {
  .moniex-bento-board {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
  }
  .moniex-premium-card {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    right: auto !important;
    width: 100% !important;
    margin-bottom: 50px !important;
  }
  .card-inga-54 {
    height: 420px !important;
  }
  .marketing-disclaimer {
    white-space: normal;
  }
}

@media (max-width: 430px) {
  .moniex-viewport {
    padding: 12px !important;
    border-radius: 16px;
  }
  .isin-info-row {
    padding-bottom: 16px;
  }
  .moniex-premium-card {
    margin-bottom: 16px !important;
    padding: 18px !important;
    border-radius: 18px !important;
  }
  .card-inga-54 {
    height: 340px !important;
    margin-bottom: 20px !important;
  }
  .card-account-mid {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
  .account-reward,
  .account-reward :deep(.inga-portrait-wrap) {
    width: 120px !important;
    height: 120px !important;
  }
  .inga-speech-bubble {
    width: calc(100% - 16px) !important;
    max-width: none !important;
    font-size: 0.78rem !important;
  }
}
</style>
